/**
 * Org group hierarchy: org → groups → group-periods → nested groups (by parent gp).
 */

import { isOwnerTeamName } from './systemOwner';
import {
  buildDerivedGuardiansGroup,
  buildDerivedGuardiansWebNode,
  DERIVED_GUARDIANS_GROUP,
  isDerivedGuardiansGroup,
  toDerivedParentGpIdSet
} from '../constants/derivedGuardians';

export { isDerivedGuardiansGroup, DERIVED_GUARDIANS_GROUP };

function appendDerivedUnderParentGp(children, parentGpId, derivedParentGpIds) {
  const set = toDerivedParentGpIdSet(derivedParentGpIds);
  if (!parentGpId || !set.has(String(parentGpId))) return children;
  return [...children, buildDerivedGuardiansWebNode(parentGpId)];
}

/** Group that owns a groups_periods id (org shell or team). */
export function resolveGroupIdForGpId(allGroups, orgGroupId, orgGpId, gpId) {
  if (!gpId) return null;
  if (orgGpId && String(gpId) === String(orgGpId)) return orgGroupId ? String(orgGroupId) : null;
  for (const g of allGroups || []) {
    if ((g.periods || []).some((p) => String(p.id) === String(gpId))) {
      return String(g.id);
    }
  }
  return null;
}

function buildGpToGroupIdMap(groups, orgGroupId, orgGpId) {
  const gpToGroupId = new Map();
  if (orgGpId && orgGroupId) {
    gpToGroupId.set(String(orgGpId), String(orgGroupId));
  }
  (groups || []).forEach((g) => {
    (g.periods || []).forEach((p) => {
      if (p?.id) gpToGroupId.set(String(p.id), String(g.id));
    });
  });
  return gpToGroupId;
}

export function resolveParentGroupId(group, orgGroupId, orgGpId, gpToGroupId) {
  const parentGp = String(group.parent_groups_periods_id || '');
  if (orgGpId && parentGp === String(orgGpId)) return String(orgGroupId);
  return gpToGroupId.get(parentGp) || (group.parent_group_id ? String(group.parent_group_id) : null);
}

export function getDirectChildGroups(groups, parentGroupId, orgGroupId, orgGpId) {
  const gpToGroupId = buildGpToGroupIdMap(groups, orgGroupId, orgGpId);
  return (groups || []).filter(
    (g) => resolveParentGroupId(g, orgGroupId, orgGpId, gpToGroupId) === String(parentGroupId)
  );
}

/** Nested groups whose relationship parent is this specific groups_periods.id */
export function getChildGroupsForParentGp(allGroups, parentGpId) {
  if (!parentGpId) return [];
  return (allGroups || []).filter((g) => {
    if (String(g.parent_groups_periods_id || '') === String(parentGpId)) return true;
    return (g.periods || []).some(
      (p) => String(p.parent_groups_periods_id || '') === String(parentGpId)
    );
  });
}

export function groupHasNestedChildren(group, allGroups, orgGroupId, orgGpId) {
  if (!group?.id) return false;
  return getDirectChildGroups(allGroups, group.id, orgGroupId, orgGpId).length > 0;
}

/** e.g. Student (2025-2026) */
export function formatGroupPathSegment(group, periodGpId = null) {
  if (!group) return '';
  let period = null;
  if (periodGpId) {
    period = (group.periods || []).find((p) => String(p.id) === String(periodGpId));
  }
  if (!period) period = group.periods?.[0];
  const periodLabel = period?.period_title || period?.title || period?.period_code;
  if (periodLabel) return `${group.name} (${periodLabel})`;
  return group.name;
}

export function periodLabel(period) {
  return period?.period_title || period?.title || period?.period_code || 'Period';
}

/** Walk parent_group_id chain from target up to (but not including) org entity. */
export function buildGroupAncestorChain(allGroups, orgGroupId, targetGroupId) {
  if (!targetGroupId || !orgGroupId) return [];
  const byId = new Map((allGroups || []).map((g) => [String(g.id), g]));
  const chain = [];
  let current = byId.get(String(targetGroupId));
  const guard = new Set();
  while (current && !guard.has(String(current.id))) {
    guard.add(String(current.id));
    chain.unshift(current);
    const parentId = current.parent_group_id ? String(current.parent_group_id) : null;
    if (!parentId || parentId === String(orgGroupId)) break;
    current = byId.get(parentId);
  }
  return chain;
}

/** Segments for UI path trail (org → groups → optional period on leaf). */
export function buildOrgGroupPathSegments(orgName, ancestorChain, leafPeriodGpId = null) {
  if (!orgName) return [];
  const chain = ancestorChain || [];
  const segments = [
    {
      key: 'org',
      label: orgName,
      groupType: 'organization',
      periodPart: null,
      isLeaf: chain.length === 0
    }
  ];
  chain.forEach((g, index) => {
    const isLast = index === chain.length - 1;
    let periodPart = null;
    if (isLast) {
      if (leafPeriodGpId) {
        const period = (g.periods || []).find((p) => String(p.id) === String(leafPeriodGpId));
        periodPart = period?.period_title || period?.title || period?.period_code || null;
      } else {
        const period = g.periods?.[0];
        periodPart = period?.period_title || period?.title || period?.period_code || null;
      }
    } else {
      const child = chain[index + 1];
      const periodGp = child?.parent_groups_periods_id;
      if (periodGp) {
        const period = (g.periods || []).find((p) => String(p.id) === String(periodGp));
        periodPart = period?.period_title || period?.title || period?.period_code || null;
      }
    }
    segments.push({
      key: `g-${g.id}`,
      label: g.name,
      groupType: g.group_type || 'team',
      periodPart,
      isLeaf: isLast
    });
  });
  if (segments.length > 1) segments[0].isLeaf = false;
  return segments;
}

/** School B > Student (2025-2026) > Choir (S1) */
export function formatOrgGroupPath(orgName, ancestorChain, leafPeriodGpId = null) {
  if (!orgName) return '';
  const parts = [orgName];
  (ancestorChain || []).forEach((g, index) => {
    const isLast = index === ancestorChain.length - 1;
    if (isLast) {
      parts.push(formatGroupPathSegment(g, leafPeriodGpId));
    } else {
      const child = ancestorChain[index + 1];
      parts.push(formatGroupPathSegment(g, child?.parent_groups_periods_id));
    }
  });
  return parts.join(' > ');
}

const sortByName = (a, b) => (a.name || '').localeCompare(b.name || '');

/** Prefer latest group row from nested-groups catalog (periods[], parent gp). */
export function resolveGroupFromAllGroups(allGroups, groupOrId) {
  if (groupOrId == null) return null;
  const id =
    typeof groupOrId === 'object' && groupOrId !== null ? groupOrId.id : groupOrId;
  if (id == null) return typeof groupOrId === 'object' ? groupOrId : null;
  const fresh = (allGroups || []).find((g) => String(g.id) === String(id));
  if (fresh) return fresh;
  return typeof groupOrId === 'object' ? groupOrId : null;
}

/** Web tree node: group (drill to periods) or period (open members; may have nested group children). */
export function buildGroupWebNode(group) {
  return {
    nodeType: 'group',
    id: `group-${group.id}`,
    group
  };
}

export function buildPeriodWebNode(group, period) {
  return {
    nodeType: 'period',
    id: `period-${period.id}`,
    group,
    period,
    periodGpId: period.id
  };
}

/**
 * Children shown under current browse focus in the web map.
 * focus: null | { group: Group }
 */
function isSystemOrganizationGroup(group) {
  if (!group) return false;
  const type = String(group.group_type || '').toLowerCase();
  const name = String(group.name || '').trim();
  return type === 'system' || name.toLowerCase() === 'system';
}

function resolveGroupParentGpId(group) {
  if (!group) return '';
  const direct = String(group.parent_groups_periods_id || '').trim();
  if (direct) return direct;
  const fromPeriod = (group.periods || []).find((p) => p?.parent_groups_periods_id);
  return fromPeriod ? String(fromPeriod.parent_groups_periods_id).trim() : '';
}

/**
 * Owner team attached directly under an org/school shell (customer org or System).
 * @param {{ systemOrgOnly?: boolean }} scope — true: System → platform Owner; false: customer org only
 */
function isOwnerDirectChildUnderOrgShell(
  group,
  allGroups,
  orgGroupId,
  orgGpId,
  organization = null,
  { systemOrgOnly = false } = {}
) {
  if (!group || !isOwnerTeamName(group.name) || !orgGroupId) return false;

  const org =
    organization ||
    (allGroups || []).find((g) => String(g.id) === String(orgGroupId));
  const isSystem = org && isSystemOrganizationGroup(org);
  if (systemOrgOnly && !isSystem) return false;
  if (!systemOrgOnly && isSystem) return false;

  const parentGpId = resolveGroupParentGpId(group);

  if (orgGpId && parentGpId && parentGpId === String(orgGpId)) {
    return true;
  }

  if (String(group.parent_group_id || '') === String(orgGroupId)) {
    return true;
  }

  const parentType = String(group.parent_group_type || '').toLowerCase();
  if (isSystem && (parentType === 'system' || parentType === 'organization')) {
    if (org?.name && String(group.parent_group_name || '').trim() === String(org.name).trim()) {
      return true;
    }
  }
  if (
    !isSystem &&
    (parentType === 'organization' || parentType === 'school') &&
    org?.name &&
    String(group.parent_group_name || '').trim() === String(org.name).trim()
  ) {
    return true;
  }

  const gpToGroupId = buildGpToGroupIdMap(allGroups, orgGroupId, orgGpId);
  const parentId = resolveParentGroupId(group, orgGroupId, orgGpId, gpToGroupId);
  return parentId != null && String(parentId) === String(orgGroupId);
}

/** Owner team under System (platform Owner). */
export function isPlatformOwnerDirectChildGroup(
  group,
  allGroups,
  orgGroupId,
  orgGpId,
  organization = null
) {
  return isOwnerDirectChildUnderOrgShell(group, allGroups, orgGroupId, orgGpId, organization, {
    systemOrgOnly: true
  });
}

/** Owner team under a customer organization (not System). */
export function isOrgOwnerDirectChildGroup(
  group,
  allGroups,
  orgGroupId,
  orgGpId,
  organization = null
) {
  return isOwnerDirectChildUnderOrgShell(group, allGroups, orgGroupId, orgGpId, organization, {
    systemOrgOnly: false
  });
}

/** Platform or organization Owner team hidden from the main web/list bridge. */
export function isShellOwnerDirectChildGroup(
  group,
  allGroups,
  orgGroupId,
  orgGpId,
  organization = null
) {
  return (
    isOrgOwnerDirectChildGroup(group, allGroups, orgGroupId, orgGpId, organization) ||
    isPlatformOwnerDirectChildGroup(group, allGroups, orgGroupId, orgGpId, organization)
  );
}

export function isSystemOrganizationGroupRecord(group) {
  return isSystemOrganizationGroup(group);
}

/** Pull shell Owner teams out of the web bridge row (crown opens them). */
export function partitionOrgOwnerWebChildren(
  treeChildren,
  allGroups,
  orgGroupId,
  orgGpId,
  organization = null
) {
  const ownerCornerNodes = [];
  const mainTreeChildren = [];
  (treeChildren || []).forEach((node) => {
    const group = node?.group;
    if (group && isShellOwnerDirectChildGroup(group, allGroups, orgGroupId, orgGpId, organization)) {
      ownerCornerNodes.push(node);
    } else {
      mainTreeChildren.push(node);
    }
  });
  return { ownerCornerNodes, mainTreeChildren };
}

function sortPeriodsNewestFirst(periods) {
  return (periods || []).slice().sort((a, b) => {
    const da = new Date(b.start_date || 0) - new Date(a.start_date || 0);
    if (da !== 0) return da;
    return periodLabel(a).localeCompare(periodLabel(b));
  });
}

/**
 * Recursive group → periods → nested groups tree for the continuous web map.
 * Avoids folder drill-down: the full hierarchy is present; the camera focuses.
 */
export function buildGroupWebSubtree(
  group,
  allGroups,
  seenGroupIds = null,
  derivedParentGpIds = null
) {
  const g = resolveGroupFromAllGroups(allGroups, group) || group;
  if (!g?.id) return null;
  const seen = seenGroupIds || new Set();
  if (seen.has(String(g.id))) {
    return buildGroupWebNode(g);
  }
  seen.add(String(g.id));

  const periods = sortPeriodsNewestFirst(g.periods);
  return {
    ...buildGroupWebNode(g),
    children: periods.map((period) => {
      const nestedGroups = getChildGroupsForParentGp(allGroups, period.id)
        .slice()
        .sort(sortByName)
        .map((nested) =>
          buildGroupWebSubtree(nested, allGroups, seen, derivedParentGpIds)
        )
        .filter(Boolean);
      return {
        ...buildPeriodWebNode(g, period),
        children: appendDerivedUnderParentGp(
          nestedGroups,
          period.id,
          derivedParentGpIds
        )
      };
    })
  };
}

export function buildWebBrowseChildren(
  allGroups,
  orgGroupId,
  orgGpId,
  focus,
  { derivedParentGpIds = null, includeDerivedGuardians = false, fullTree = true } = {}
) {
  // Prefer explicit placement parents; legacy boolean → hang off org gp only.
  const placementIds =
    derivedParentGpIds != null
      ? derivedParentGpIds
      : includeDerivedGuardians && orgGpId
        ? [orgGpId]
        : null;

  // Continuous map: expand group → period → nested group from the org root.
  // `focus` is ignored for structure (camera focus lives in the view layer).
  if (fullTree) {
    const teamChildren = getDirectChildGroups(allGroups, orgGroupId, orgGroupId, orgGpId)
      .slice()
      .sort(sortByName)
      .map((group) => buildGroupWebSubtree(group, allGroups, null, placementIds))
      .filter(Boolean);
    return appendDerivedUnderParentGp(teamChildren, orgGpId, placementIds);
  }

  if (!focus?.group) {
    const teamChildren = getDirectChildGroups(allGroups, orgGroupId, orgGroupId, orgGpId)
      .slice()
      .sort(sortByName)
      .map(buildGroupWebNode);
    return appendDerivedUnderParentGp(teamChildren, orgGpId, placementIds);
  }

  const group = resolveGroupFromAllGroups(allGroups, focus.group);
  if (!group) return [];
  return sortPeriodsNewestFirst(group.periods).map((period) => {
    const nestedGroups = getChildGroupsForParentGp(allGroups, period.id)
      .slice()
      .sort(sortByName)
      .map(buildGroupWebNode);
    return {
      ...buildPeriodWebNode(group, period),
      children: appendDerivedUnderParentGp(nestedGroups, period.id, placementIds)
    };
  });
}

/** Nested group nodes under a period branch (for rendering). */
export function getPeriodBranchChildren(periodNode) {
  return periodNode?.children || [];
}

/** Flat list rows: group → indented periods → indented nested groups under each period. */
export function flattenOrgBrowseList(
  allGroups,
  orgGroupId,
  orgGpId,
  focus,
  acc = [],
  depth = 0,
  organization = null,
  { derivedParentGpIds = null, includeDerivedGuardians = false } = {}
) {
  const placementIds =
    derivedParentGpIds != null
      ? derivedParentGpIds
      : includeDerivedGuardians && orgGpId
        ? [orgGpId]
        : null;
  const placementSet = toDerivedParentGpIdSet(placementIds);

  if (!focus?.group) {
    getDirectChildGroups(allGroups, orgGroupId, orgGroupId, orgGpId)
      .slice()
      .sort(sortByName)
      .forEach((group) => {
        if (
          organization &&
          isShellOwnerDirectChildGroup(group, allGroups, orgGroupId, orgGpId, organization)
        ) {
          return;
        }
        acc.push({ kind: 'group', group, depth, period: null, periodGpId: null });
        flattenOrgBrowseList(
          allGroups,
          orgGroupId,
          orgGpId,
          { group },
          acc,
          depth + 1,
          organization,
          { derivedParentGpIds: placementIds }
        );
      });
    if (orgGpId && placementSet.has(String(orgGpId))) {
      acc.push({
        kind: 'group',
        group: buildDerivedGuardiansGroup(orgGpId),
        depth,
        period: null,
        periodGpId: null,
        isDerivedGuardians: true,
        derivedParentGpId: orgGpId
      });
    }
    return acc;
  }

  const group = resolveGroupFromAllGroups(allGroups, focus.group);
  if (!group) return acc;
  (group.periods || [])
    .slice()
    .sort((a, b) => new Date(b.start_date || 0) - new Date(a.start_date || 0))
    .forEach((period) => {
      acc.push({
        kind: 'period',
        group,
        period,
        periodGpId: period.id,
        depth
      });
      getChildGroupsForParentGp(allGroups, period.id)
        .slice()
        .sort(sortByName)
        .forEach((nested) => {
          const nestedFresh = resolveGroupFromAllGroups(allGroups, nested) || nested;
          acc.push({
            kind: 'group',
            group: nestedFresh,
            depth: depth + 1,
            period: null,
            periodGpId: null
          });
          flattenOrgBrowseList(
            allGroups,
            orgGroupId,
            orgGpId,
            { group: nestedFresh },
            acc,
            depth + 2,
            organization,
            { derivedParentGpIds: placementIds }
          );
        });
      if (period.id && placementSet.has(String(period.id))) {
        acc.push({
          kind: 'group',
          group: buildDerivedGuardiansGroup(period.id),
          depth: depth + 1,
          period: null,
          periodGpId: null,
          isDerivedGuardians: true,
          derivedParentGpId: period.id
        });
      }
    });
  return acc;
}

/** @deprecated use buildWebBrowseChildren — kept for any legacy imports */
export function buildOrgGroupTree(groups, orgGroupId, orgGpId) {
  if (!orgGroupId || !orgGpId || !Array.isArray(groups) || groups.length === 0) {
    return [];
  }
  const gpToGroupId = buildGpToGroupIdMap(groups, orgGroupId, orgGpId);
  const byParentGroupId = new Map();
  groups.forEach((g) => {
    const parentGroupId = resolveParentGroupId(g, orgGroupId, orgGpId, gpToGroupId);
    if (!parentGroupId) return;
    if (!byParentGroupId.has(parentGroupId)) byParentGroupId.set(parentGroupId, []);
    byParentGroupId.get(parentGroupId).push(g);
  });
  function buildNode(group) {
    const kids = (byParentGroupId.get(String(group.id)) || []).slice().sort(sortByName);
    return { ...group, children: kids.map(buildNode) };
  }
  return (byParentGroupId.get(String(orgGroupId)) || []).slice().sort(sortByName).map(buildNode);
}

export function findTreeNode(nodes, groupId) {
  if (!groupId) return null;
  for (const node of nodes || []) {
    if (String(node.id) === String(groupId)) return node;
    const found = findTreeNode(node.children, groupId);
    if (found) return found;
  }
  return null;
}
